import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PlaylistAdd as PlaylistAddIcon,
  Visibility as VisibilityIcon,
  Home as HomeIcon,
  MedicalServices as MedicalIcon,
  AttachFile as AttachFileIcon,
  DeleteForever as DeleteIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import api from '../services/api';
import DateField from './DateField';
import HelpButton from './HelpButton';
import MunicipalityExpenseDialog from './MunicipalityExpenseDialog';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface ExternalCat {
  id: number;
  chip_id: string | number | null;
  name: string | null;
  sex: string | null;
  municipality: string | null;
  sterilized: string | null;
}

const ExternalCatsList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [cats, setCats] = useState<ExternalCat[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedExternal, setSelectedExternal] = useState<ExternalCat | null>(null);

  // Single add
  const [addOpen, setAddOpen] = useState(false);
  const [chip, setChip] = useState('');
  const [name, setName] = useState('');
  const [sex, setSex] = useState('');
  const [muni, setMuni] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit an existing external cat
  const [editOpen, setEditOpen] = useState(false);
  const [editCat, setEditCat] = useState<ExternalCat | null>(null);
  const [editChip, setEditChip] = useState('');
  const [editName, setEditName] = useState('');
  const [editSex, setEditSex] = useState('');
  const [editMuni, setEditMuni] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Bulk add
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMuni, setBulkMuni] = useState('');
  const [bulkChips, setBulkChips] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState<any>(null);

  // Admit to shelter (external -> active)
  const [admitOpen, setAdmitOpen] = useState(false);
  const [admitCat, setAdmitCat] = useState<ExternalCat | null>(null);
  const [admitDate, setAdmitDate] = useState('');
  const [admitSaving, setAdmitSaving] = useState(false);

  // Add expense (medical) event
  const [eventOpen, setEventOpen] = useState(false);
  const [eventCat, setEventCat] = useState<ExternalCat | null>(null);
  const [eventForm, setEventForm] = useState<any>({});
  const [eventFile, setEventFile] = useState<File | null>(null);
  const [eventSaving, setEventSaving] = useState(false);
  const [eventError, setEventError] = useState<string | null>(null);
  const [sexPromptOpen, setSexPromptOpen] = useState(false);
  // Sterilization only: unticked when the municipality refuses the recovery.
  const [eventRecoveryWaived, setEventRecoveryWaived] = useState(false);

  // Delete cat entirely
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteCat, setDeleteCat] = useState<ExternalCat | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  // Generic / multi-cat invoice: handled by the shared dialog.
  const [ceOpen, setCeOpen] = useState(false);

  // Resizable / sortable table (matches the other list pages)
  const [colWidths, setColWidths] = useState<Record<string, number>>({
    chip_id: 150, name: 150, sex: 60, municipality: 150, sterilized: 100,
  });
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (col: string) => {
    if (sortCol === col) { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); }
    else { setSortCol(col); setSortDir('asc'); }
  };
  const resizingRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null);
  const handleResizeMouseDown = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { col, startX: e.clientX, startWidth: colWidths[col] };
    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const newWidth = Math.max(40, resizingRef.current.startWidth + ev.clientX - resizingRef.current.startX);
      setColWidths(prev => ({ ...prev, [resizingRef.current!.col]: newWidth }));
    };
    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [colWidths]);

  const flash = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadCats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/tables/public/cats/data?external=true&limit=1000`);
      const cols: string[] = response.data.columns || [];
      const idx = (n: string) => cols.indexOf(n);
      const list: ExternalCat[] = (response.data.rows || []).map((r: any[]) => ({
        id: r[idx('id')],
        chip_id: r[idx('chip_id')],
        name: r[idx('name')],
        sex: r[idx('sex')],
        municipality: r[idx('municipality')],
        sterilized: r[idx('sterilized')],
      }));
      setCats(list);
      // Keep the detail panel in sync with the refreshed data.
      setSelectedExternal(prev => (prev ? list.find(c => c.id === prev.id) || null : null));
    } catch (e) {
      console.error('Error loading external cats:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMunicipalities = useCallback(async () => {
    try {
      const response = await api.get(`/api/tables/public/municipalities/data?limit=1000`);
      const data = response.data.rows.map((row: any[]) => row[0]);
      setMunicipalities(data.sort());
    } catch (e) {
      console.error('Error loading municipalities:', e);
    }
  }, []);

  useEffect(() => {
    loadCats();
    loadMunicipalities();
  }, [loadCats, loadMunicipalities]);

  const sortedCats = useMemo(() => {
    if (!sortCol) return cats;
    return [...cats].sort((a, b) => {
      const ar = (a as any)[sortCol] ?? '';
      const br = (b as any)[sortCol] ?? '';
      const an = Number(ar), bn = Number(br);
      if (!isNaN(an) && !isNaN(bn) && ar !== '' && br !== '') {
        return sortDir === 'asc' ? an - bn : bn - an;
      }
      const av = String(ar).toLowerCase();
      const bv = String(br).toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [cats, sortCol, sortDir]);

  const openAdd = () => {
    setChip('');
    setName('');
    setSex('');
    setMuni('');
    setAddOpen(true);
  };

  const handleAddSingle = async () => {
    const cleanChip = chip.trim();
    if (!cleanChip || !/^\d+$/.test(cleanChip)) {
      flash('error', t('external.invalidChip'));
      return;
    }
    if (!muni) {
      flash('error', t('external.municipalityRequired'));
      return;
    }
    setSaving(true);
    try {
      await api.post(`/api/records`, {
        table: 'cats',
        schema: 'public',
        data: {
          chip_id: Number(cleanChip),
          name: name.trim() || null,
          sex: sex || null,
          municipality: muni,
          status: 'active',
          external: true,
        },
      });
      setAddOpen(false);
      flash('success', t('external.addedSingle'));
      loadCats();
    } catch (e: any) {
      flash('error', e?.response?.data?.detail || t('external.addFailed'));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (cat: ExternalCat) => {
    setEditCat(cat);
    setEditChip(cat.chip_id != null ? String(cat.chip_id) : '');
    setEditName(cat.name || '');
    setEditSex(cat.sex || '');
    setEditMuni(cat.municipality || '');
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editCat) return;
    const cleanChip = editChip.trim();
    // The chip is optional here (some external cats have none), but when it is
    // filled in it must still be a plain number.
    if (cleanChip && !/^\d+$/.test(cleanChip)) {
      flash('error', t('external.invalidChip'));
      return;
    }
    if (!editMuni) {
      flash('error', t('external.municipalityRequired'));
      return;
    }
    setEditSaving(true);
    try {
      await api.put(`/api/records`, {
        table: 'cats',
        schema: 'public',
        record_id: editCat.id,
        data: {
          chip_id: cleanChip ? Number(cleanChip) : null,
          name: editName.trim() || null,
          sex: editSex || null,
          municipality: editMuni,
        },
      });
      setEditOpen(false);
      flash('success', t('external.updated'));
      loadCats();
    } catch (e: any) {
      flash('error', e?.response?.data?.detail || t('external.updateFailed'));
    } finally {
      setEditSaving(false);
    }
  };

  const openBulk = () => {
    setBulkMuni('');
    setBulkChips('');
    setBulkResult(null);
    setBulkOpen(true);
  };

  const handleBulk = async () => {
    if (!bulkMuni) {
      flash('error', t('external.municipalityRequired'));
      return;
    }
    const chips = bulkChips
      .split('\n')
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    if (chips.length === 0) {
      flash('error', t('external.noChips'));
      return;
    }
    setBulkSaving(true);
    setBulkResult(null);
    try {
      const response = await api.post(`/api/cats/bulk-external`, {
        municipality: bulkMuni,
        chips,
      });
      setBulkResult(response.data);
      loadCats();
    } catch (e: any) {
      flash('error', e?.response?.data?.detail || t('external.addFailed'));
    } finally {
      setBulkSaving(false);
    }
  };

  const openAdmit = (cat: ExternalCat) => {
    setAdmitCat(cat);
    setAdmitDate(todayStr());
    setAdmitOpen(true);
  };

  const handleAdmit = async () => {
    if (!admitCat) return;
    if (!admitDate) {
      flash('error', t('external.admitDateRequired'));
      return;
    }
    setAdmitSaving(true);
    try {
      await api.post(`/api/cats/${admitCat.id}/to-shelter`, { admission_date: admitDate });
      setAdmitOpen(false);
      setSelectedExternal(null);
      flash('success', t('external.admitted'));
      loadCats();
    } catch (e: any) {
      flash('error', e?.response?.data?.detail || t('external.addFailed'));
    } finally {
      setAdmitSaving(false);
    }
  };

  const openEvent = (cat: ExternalCat) => {
    setEventRecoveryWaived(false);
    setEventCat(cat);
    setEventForm({ event_date: todayStr() });
    setEventFile(null);
    setEventError(null);
    setEventOpen(true);
  };

  const handleSaveEvent = async (sexOverride?: string) => {
    if (!eventCat) return;
    if (!eventForm.event_type) {
      flash('error', t('external.eventTypeRequired'));
      return;
    }
    // Sterilization triggers recovery days; the cat's sex determines the duration.
    const isSterilization = eventForm.event_type === 'Sterilization';
    const recoverySex = eventCat.sex || sexOverride || eventForm.recoverySex;
    if (isSterilization && !recoverySex) {
      // Ask for the sex on the fly via a popup, then resume the save.
      setSexPromptOpen(true);
      return;
    }
    setEventError(null);
    setEventSaving(true);
    try {
      let filePath: string | null = null;
      if (eventFile) {
        const fd = new FormData();
        fd.append('file', eventFile);
        const up = await api.post(`/api/upload/invoice`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        filePath = up.data.file_path;
      }
      const created = await api.post(`/api/records`, {
        table: 'medical',
        schema: 'public',
        data: {
          cat_id: eventCat.id,
          event_type: eventForm.event_type,
          event_date: eventForm.event_date || null,
          cost: eventForm.cost || null,
          veterinary_name: eventForm.veterinary_name || null,
          notes: eventForm.notes || null,
          recovery_waived: isSterilization ? eventRecoveryWaived : false,
          ...(filePath && { invoice_file_path: filePath }),
        },
      });
      // Record the recovery decision: the stay is created only when the
      // municipality pays it, but the choice is stored either way.
      const medicalId = created?.data?.data?.id;
      if (isSterilization && eventForm.event_date && medicalId) {
        await api.put(`/api/medical/${medicalId}/recovery`, {
          waived: eventRecoveryWaived,
          sex: recoverySex,
        });
      }
      setEventOpen(false);
      flash('success', !isSterilization
        ? t('external.eventAdded')
        : eventRecoveryWaived
          ? t('external.eventAddedNoRecovery')
          : t('external.eventAddedWithRecovery'));
      loadCats();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || '';
      if (typeof detail === 'string' && detail.includes('idx_unique_sterilization_per_cat')) {
        setEventError(t('external.sterilizationDuplicate'));
      } else {
        setEventError(detail || t('external.addFailed'));
      }
    } finally {
      setEventSaving(false);
    }
  };

  const openDelete = (cat: ExternalCat) => {
    setDeleteCat(cat);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteCat) return;
    setDeleteSaving(true);
    try {
      await api.delete(`/api/records`, {
        data: { table: 'cats', schema: 'public', record_id: deleteCat.id },
      });
      setDeleteOpen(false);
      setSelectedExternal(null);
      flash('success', t('external.deleted'));
      loadCats();
    } catch (e: any) {
      flash('error', e?.response?.data?.detail || t('external.addFailed'));
    } finally {
      setDeleteSaving(false);
    }
  };

  const EVENT_TYPES = [
    { value: 'Vaccine', label: t('medical.eventTypes.vaccine') },
    { value: 'Sterilization', label: t('medical.eventTypes.sterilization') },
    { value: 'Treatment', label: t('medical.eventTypes.treatment') },
    { value: 'Delivery', label: t('medical.eventTypes.delivery') },
    { value: 'Surgery', label: t('medical.eventTypes.surgery') },
    { value: 'Checkup', label: t('medical.eventTypes.checkup') },
    { value: 'test_fiv_felv', label: t('medical.eventTypes.testFivFelv') },
    { value: 'Travel Expenses', label: t('medical.eventTypes.travelExpenses') },
    { value: 'Other', label: t('medical.eventTypes.other') },
  ];

  const SORT_COLS = [
    { col: 'chip_id', label: t('external.chip') },
    { col: 'name', label: t('external.name') },
    { col: 'sex', label: t('external.sex') },
    { col: 'municipality', label: t('external.municipality') },
    { col: 'sterilized', label: t('catFields.sterilized') },
  ] as const;

  const sterilizedLabel = (v: string | null | undefined): string =>
    v === 'Y' ? t('common.yes') : v === 'N' ? t('common.no') : t('common.unknown');

  const detailField = (label: string, value: React.ReactNode) => (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || '-'}</Typography>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#667eea' }}>
              {t('external.title')}
            </Typography>
            <HelpButton
              title={t('help.external.title')}
              intro={t('help.external.intro')}
              items={t('help.external.items', { returnObjects: true }) as string[]}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">{t('external.subtitle')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Add cats */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<PlaylistAddIcon />} onClick={openBulk} sx={{ textTransform: 'none', fontWeight: 600 }}>
              {t('external.bulkAdd')}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ textTransform: 'none', fontWeight: 600 }}>
              {t('external.addSingle')}
            </Button>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
          {/* Cumulative expense */}
          <Button variant="outlined" color="secondary" startIcon={<AccountBalanceIcon />} onClick={() => setCeOpen(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('external.addCumulative')}
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, height: 'calc(100vh - 220px)', minHeight: 300 }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            flex: selectedExternal ? '0 0 58%' : '1 1 100%',
            transition: 'flex 0.3s ease',
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(102,126,234,0.15)',
            overflow: 'auto',
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 36 }} />
              {SORT_COLS.map(({ col }) => (
                <col key={col} style={{ width: colWidths[col] }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow sx={{ '& th': { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 600, fontSize: '0.75rem', padding: '6px 10px', position: 'relative', overflow: 'hidden', whiteSpace: 'nowrap' } }}>
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400 }}>#</TableCell>
                {SORT_COLS.map(({ col, label }) => (
                  <TableCell key={col} onClick={() => handleSort(col)} sx={{ cursor: 'pointer', userSelect: 'none' }}>
                    <TableSortLabel
                      active={sortCol === col}
                      direction={sortCol === col ? sortDir : 'asc'}
                      sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.7) !important' } }}
                    >
                      {label}
                    </TableSortLabel>
                    <Box
                      onMouseDown={(e) => handleResizeMouseDown(col, e)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: 'absolute', right: 0, top: 0, bottom: 0, width: 5,
                        cursor: 'col-resize', zIndex: 1,
                        '&:hover': { background: 'rgba(255,255,255,0.35)' },
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
              ) : sortedCats.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary" sx={{ py: 2 }}>{t('external.empty')}</Typography></TableCell></TableRow>
              ) : (
                sortedCats.map((c, index) => (
                  <TableRow
                    key={c.id}
                    hover
                    onClick={() => setSelectedExternal(c)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedExternal?.id === c.id ? 'rgba(102,126,234,0.12)' : 'inherit',
                      borderLeft: selectedExternal?.id === c.id ? '3px solid #667eea' : '3px solid transparent',
                      '& td': { padding: '4px 8px', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 },
                      '&:hover': { backgroundColor: selectedExternal?.id === c.id ? 'rgba(102,126,234,0.18)' : 'rgba(0,0,0,0.04)' },
                    }}
                  >
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem', userSelect: 'none' }}>{index + 1}</TableCell>
                    <TableCell>{c.chip_id || t('cat.missing')}</TableCell>
                    <TableCell><strong>{c.name || '-'}</strong></TableCell>
                    <TableCell>{c.sex || '-'}</TableCell>
                    <TableCell>{c.municipality || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={c.sterilized === 'Y' ? 'Y' : c.sterilized === 'N' ? 'N' : '?'}
                        size="small"
                        color={c.sterilized === 'Y' ? 'success' : c.sterilized === 'N' ? 'error' : 'default'}
                        sx={{ fontWeight: 600, minWidth: 32 }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Detail panel (right side) */}
        {selectedExternal && (
          <Paper
            elevation={0}
            sx={{
              flex: '0 0 40%',
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(102,126,234,0.12)',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #a78bfa 100%)', p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
                  {selectedExternal.name || t('external.title')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  {t('external.chip')}: {selectedExternal.chip_id || t('cat.missing')}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelectedExternal(null)} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, borderBottom: '1px solid #eee' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => openEdit(selectedExternal)}
                sx={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('common.edit')}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<MedicalIcon />}
                onClick={() => openEvent(selectedExternal)}
                sx={{ flex: 1, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('medical.medicalEvent')}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<HomeIcon />}
                onClick={() => openAdmit(selectedExternal)}
                sx={{ flex: 1, background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('external.admit')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate(`/cat-record/${selectedExternal.id}`)}
                sx={{ flex: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('external.openRecord')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => openDelete(selectedExternal)}
                sx={{ flex: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('external.delete')}
              </Button>
            </Box>

            {/* Cat details */}
            <Box sx={{ p: 2 }}>
              {detailField(t('external.name'), selectedExternal.name)}
              {detailField(t('external.sex'), selectedExternal.sex)}
              {detailField(t('external.municipality'), selectedExternal.municipality)}
              {detailField(t('catFields.sterilized'), sterilizedLabel(selectedExternal.sterilized))}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Single add dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('external.addSingle')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('external.chip')}
            value={chip}
            onChange={(e) => setChip(e.target.value)}
            required
            autoFocus
            inputProps={{ inputMode: 'numeric' }}
          />
          <FormControl fullWidth required>
            <InputLabel>{t('external.municipality')}</InputLabel>
            <Select label={t('external.municipality')} value={muni} onChange={(e) => setMuni(e.target.value)}>
              {municipalities.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label={t('external.name')} value={name} onChange={(e) => setName(e.target.value)} />
          <FormControl fullWidth>
            <InputLabel>{t('external.sex')}</InputLabel>
            <Select label={t('external.sex')} value={sex} onChange={(e) => setSex(e.target.value)}>
              <MenuItem value=""><em>-</em></MenuItem>
              <MenuItem value="M">M</MenuItem>
              <MenuItem value="F">F</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAddSingle} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('external.editCat')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('external.chip')}
            value={editChip}
            onChange={(e) => setEditChip(e.target.value)}
            autoFocus
            inputProps={{ inputMode: 'numeric' }}
            helperText={t('external.chipOptionalHelp')}
          />
          <FormControl fullWidth required>
            <InputLabel>{t('external.municipality')}</InputLabel>
            <Select label={t('external.municipality')} value={editMuni} onChange={(e) => setEditMuni(e.target.value)}>
              {municipalities.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label={t('external.name')} value={editName} onChange={(e) => setEditName(e.target.value)} />
          <FormControl fullWidth>
            <InputLabel>{t('external.sex')}</InputLabel>
            <Select label={t('external.sex')} value={editSex} onChange={(e) => setEditSex(e.target.value)}>
              <MenuItem value=""><em>-</em></MenuItem>
              <MenuItem value="M">M</MenuItem>
              <MenuItem value="F">F</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk add dialog */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('external.bulkAdd')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth required>
            <InputLabel>{t('external.municipality')}</InputLabel>
            <Select label={t('external.municipality')} value={bulkMuni} onChange={(e) => setBulkMuni(e.target.value)}>
              {municipalities.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label={t('external.bulkChipsLabel')}
            value={bulkChips}
            onChange={(e) => setBulkChips(e.target.value)}
            multiline
            minRows={6}
            placeholder={t('external.bulkChipsPlaceholder')}
            helperText={t('external.bulkChipsHelp')}
          />
          {bulkResult && (
            <Alert severity="success">
              {t('external.bulkResult', {
                created: bulkResult.created_count,
                skipped: bulkResult.skipped_count,
                invalid: bulkResult.invalid_count,
              })}
              {bulkResult.invalid_count > 0 && (
                <Box sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                  {t('external.bulkInvalidList')}: {bulkResult.invalid.join(', ')}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>{t('common.close')}</Button>
          <Button variant="contained" onClick={handleBulk} disabled={bulkSaving}>
            {bulkSaving ? <CircularProgress size={20} /> : t('external.bulkCreate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admit to shelter dialog */}
      <Dialog open={admitOpen} onClose={() => setAdmitOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('external.admit')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('external.admitConfirm', { name: admitCat?.name || admitCat?.chip_id || '' })}
          </Typography>
          <DateField label={t('external.admissionDate')} value={admitDate} onChange={(v: string) => setAdmitDate(v)} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('external.admitHelp')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdmitOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAdmit} disabled={admitSaving}>
            {admitSaving ? <CircularProgress size={20} /> : t('external.admit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Medical Event Dialog (same style as the other list pages) */}
      <Dialog
        open={eventOpen}
        onClose={() => setEventOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(102,126,234,0.25)' } }}
      >
        <DialogTitle sx={{ py: 1.5, fontSize: '1rem' }}>
          {t('medical.addMedicalEventFor')}: {eventCat?.name || eventCat?.chip_id}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', pb: 1, px: 2 }}>
          {eventError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setEventError(null)}>{eventError}</Alert>}
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('medical.eventType')} *</InputLabel>
            <Select
              value={eventForm.event_type || ''}
              label={`${t('medical.eventType')} *`}
              onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
              required
            >
              {EVENT_TYPES.map((et) => <MenuItem key={et.value} value={et.value}>{et.label}</MenuItem>)}
            </Select>
          </FormControl>
          {eventForm.event_type === 'Sterilization' && (
            <>
              <Alert severity="info">{t('external.recoveryInfo')}</Alert>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={!eventRecoveryWaived}
                    onChange={(e) => setEventRecoveryWaived(!e.target.checked)}
                  />
                )}
                label={(
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('sterilizationBatch.recoveryRecognised')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('sterilizationBatch.recoveryRecognisedHint')}
                    </Typography>
                  </Box>
                )}
              />
            </>
          )}
          <DateField
            label={t('medical.eventDate')}
            value={eventForm.event_date || ''}
            onChange={(v: string) => setEventForm({ ...eventForm, event_date: v })}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('medical.cost')}
            type="text"
            inputMode="decimal"
            value={eventForm.cost || ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setEventForm({ ...eventForm, cost: v });
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('medical.veterinaryName')}
            value={eventForm.veterinary_name || ''}
            onChange={(e) => setEventForm({ ...eventForm, veterinary_name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label={t('cat.notes')}
            multiline
            rows={3}
            value={eventForm.notes || ''}
            onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
          />

          {/* Invoice Upload Section */}
          <Box sx={{ mt: 3, p: 2, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#f9f9f9' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              {t('medical.invoiceUploadOptional')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {t('medical.uploadDocument')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button variant="contained" component="label" startIcon={<AttachFileIcon />} color="primary" sx={{ flexGrow: 1 }}>
                {t('common.browseFiles')}
                <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => { if (e.target.files && e.target.files[0]) setEventFile(e.target.files[0]); }} />
              </Button>
              {eventFile && (
                <Button variant="outlined" color="error" size="small" onClick={() => setEventFile(null)}>
                  {t('common.clear')}
                </Button>
              )}
            </Box>
            {eventFile ? (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" fontWeight="medium" color="primary">{t('common.fileSelected')}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{eventFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">{t('common.size')}: {(eventFile.size / 1024).toFixed(2)} KB</Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                {t('common.noFileSelected')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => handleSaveEvent()}
            variant="contained"
            disabled={eventSaving || !eventForm.event_type}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(240,147,251,0.4)',
              '&:hover': { background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)' },
              '&:disabled': { background: '#ccc', color: '#666' },
            }}
          >
            {eventSaving ? <CircularProgress size={20} /> : t('medical.addEvent')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sex prompt popup (asked on the fly for sterilization when sex is missing) */}
      <Dialog open={sexPromptOpen} onClose={() => setSexPromptOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('external.recoverySexLabel')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" sx={{ mb: 2 }}>{t('external.recoverySexRequired')}</Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { setSexPromptOpen(false); handleSaveEvent('M'); }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('external.sexMale')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={() => { setSexPromptOpen(false); handleSaveEvent('F'); }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('external.sexFemale')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSexPromptOpen(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete cat dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>{t('external.delete')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('external.deleteConfirm', { name: deleteCat?.name || deleteCat?.chip_id || `#${deleteCat?.id}` })}
          </Typography>
          <Alert severity="warning">{t('external.deleteWarning')}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteSaving}>
            {deleteSaving ? <CircularProgress size={20} /> : t('external.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generic / multi-cat invoice: same dialog used by the Spese Mediche page */}
      <MunicipalityExpenseDialog
        open={ceOpen}
        onClose={() => setCeOpen(false)}
        municipalities={municipalities}
        expense={null}
        onSaved={(msg) => { flash('success', msg); loadCats(); }}
      />
    </Container>
  );
};

export default ExternalCatsList;
